import { deploymentIdentity } from "@pima/web-api/deployment";
import type { VersionCheckUnavailable } from "./deployed-version";
import { Effect } from "effect";
import type { RefreshUnavailable } from "./refresh";

export interface UpdateNoticeSnapshot {
  readonly visible: boolean;
  readonly preparing: boolean;
  readonly failed: boolean;
}

/** View state of the app-update notice; the component renders its snapshot. */
export function makeUpdateNotice(deps: {
  readonly browserRelease?: string | null;
  readonly readRelease?: Effect.Effect<string | null, VersionCheckUnavailable>;
  readonly refresh: Effect.Effect<void, RefreshUnavailable>;
  readonly reportDefect: (error: unknown) => void;
}) {
  // The deployed release the page is behind, or null while it is current.
  let behind: string | null = null;
  // The release whose notice the person closed. A newer release is news again.
  let dismissed: string | null = null;
  let preparing = false;
  let failed = false;
  let checking = false;
  const listeners = new Set<() => void>();
  let snapshot: UpdateNoticeSnapshot = {
    visible: false,
    preparing,
    failed,
  };
  const publish = () => {
    const visible = behind !== null && behind !== dismissed;
    if (
      visible === snapshot.visible &&
      preparing === snapshot.preparing &&
      failed === snapshot.failed
    )
      return;
    snapshot = { visible, preparing, failed };
    for (const listener of [...listeners]) listener();
  };
  const observe = (mismatch: boolean, deployed: string | null) => {
    behind = mismatch ? deployed : null;
    if (!mismatch) {
      dismissed = null;
      failed = false;
    }
    publish();
  };
  return {
    checking: () => checking,
    check: Effect.suspend(() => {
      const current = deploymentIdentity(deps.browserRelease);
      if (checking || current === null || !deps.readRelease) return Effect.void;
      checking = true;
      return deps.readRelease.pipe(
        Effect.tap((release) =>
          Effect.sync(() => {
            const deployed = deploymentIdentity(release);
            if (deployed !== null) observe(deployed !== current, deployed);
          }),
        ),
        Effect.catchAll(() => Effect.void),
        Effect.catchAllDefect((error) =>
          Effect.sync(() => deps.reportDefect(error)),
        ),
        Effect.ensuring(
          Effect.sync(() => {
            checking = false;
          }),
        ),
      );
    }),
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,
    /** The last confirmed comparison with the deployed web build. */
    observe,
    dismiss() {
      dismissed = behind;
      publish();
    },
    /** One attempt at a time; a failed attempt leaves the page as it was. */
    refresh: Effect.suspend(() => {
      if (preparing) return Effect.void;
      preparing = true;
      failed = false;
      publish();
      return deps.refresh.pipe(
        Effect.catchAll(() =>
          Effect.sync(() => {
            failed = true;
          }),
        ),
        Effect.catchAllDefect((error) =>
          Effect.sync(() => {
            deps.reportDefect(error);
            failed = true;
          }),
        ),
        Effect.ensuring(
          Effect.sync(() => {
            preparing = false;
            publish();
          }),
        ),
      );
    }),
  };
}
